import { useCallback, useEffect, useState } from "react";

import { LoginPage } from "./pages/Login";
import { OverviewPage } from "./pages/Overview";
import { StrategyDetailPage } from "./pages/StrategyDetail";
import { ApiError, api } from "./lib/api";
import { STRATEGIES, type StrategyKey } from "./lib/types";

type Route =
  | { name: "overview" }
  | { name: "strategy"; strategy: StrategyKey };

function parseHash(hash: string): Route {
  // Routes:
  //   #/                  -> overview
  //   #/s/<short>         -> strategy detail
  const m = /^#\/s\/([a-z]+)$/.exec(hash);
  if (m) {
    const s = m[1] as StrategyKey;
    if (STRATEGIES.includes(s)) return { name: "strategy", strategy: s };
  }
  return { name: "overview" };
}

export function App() {
  const [route, setRoute] = useState<Route>(() => parseHash(location.hash));
  const [authed, setAuthed] = useState<"unknown" | "yes" | "no">("unknown");

  // Initial probe — call /status; if 401 we need login, if 200 we're in.
  useEffect(() => {
    let cancelled = false;
    api
      .status()
      .then(() => !cancelled && setAuthed("yes"))
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 401) setAuthed("no");
        else setAuthed("yes"); // network blip — try anyway, the page will retry
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onHash = () => setRoute(parseHash(location.hash));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const onOpenStrategy = useCallback((s: StrategyKey) => {
    location.hash = `#/s/${s}`;
  }, []);
  const onBack = useCallback(() => {
    location.hash = "#/";
  }, []);
  const onAuthFail = useCallback(() => setAuthed("no"), []);
  const onLoggedIn = useCallback(() => setAuthed("yes"), []);

  if (authed === "unknown") {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-slate-500">
        loading…
      </div>
    );
  }
  if (authed === "no") return <LoginPage onLoggedIn={onLoggedIn} />;

  if (route.name === "strategy") {
    return (
      <StrategyDetailPage
        strategy={route.strategy}
        onBack={onBack}
        onAuthFail={onAuthFail}
      />
    );
  }
  return (
    <OverviewPage onOpenStrategy={onOpenStrategy} onAuthFail={onAuthFail} />
  );
}
